import { ChangeDetectorRef, Component, EventEmitter, Inject, OnInit } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { OptionsCardSelect } from 'src/app/core/interfaces/interfaces';
import { RequestService } from 'src/app/core/services/modules/request.service';

export interface DialogData {
  optionsCardSelect: OptionsCardSelect;
  title: string;
  data: any[];
  adminId: string;
  selectedJobTypeId?: string | null;
  groupSelections?: Array<{ groupId: any; optionId: any }> | null;
}

@Component({
  selector: 'app-dialog-wizard-search-resquest-id',
  templateUrl: './dialog-wizard-search-resquest-id.component.html',
  styleUrls: ['./dialog-wizard-search-resquest-id.component.scss']
})
export class DialogWizardSearchResquestIdComponent implements OnInit {

  positionTypeConfirmed = new EventEmitter<any>();

  stepIndex = 0;

  ctrlPositionType = new FormControl(null, Validators.required);

  jobTypes: any[] = [];
  jobTypesLoading = false;

  groups: any[] = [];
  groupsLoading = false;
  groupControls: FormControl[] = [];
  displayOptions: any[] = [];

  private committedPositionTypeId: any = null;
  private jobTypeDetail: any = null;

  constructor(
    public dialogRef: MatDialogRef<DialogWizardSearchResquestIdComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    private requestService: RequestService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    // Si el padre ya envió la lista precargada en data.data, usarla directamente
    // para evitar una llamada duplicada a la API de compliance.
    if (this.data.data && this.data.data.length > 0) {
      this.jobTypes = this.data.data;
    } else if (this.data.adminId) {
      this.jobTypesLoading = true;
      this.requestService.getComplianceJobTypes(this.data.adminId).subscribe({
        next: (res) => {
          const raw = res?.data;
          const list: any[] = Array.isArray(raw) ? raw : (Array.isArray(raw?.jobTypes) ? raw.jobTypes : []);
          const apiJobTypes = list.map((jt: any) => ({ Id: jt.id, Name: jt.name }));
          this.jobTypes = apiJobTypes.length > 0 ? apiJobTypes : [];
          this.jobTypesLoading = false;
        },
        error: () => {
          this.jobTypes = [];
          this.jobTypesLoading = false;
        }
      });
    }

    // Si ya había una selección previa, pre-seleccionar en step 0 para que el usuario
    // la vea. El wizard permanece en step 0; el usuario decide si cambiarla o presiona Siguiente.
    if (this.data.selectedJobTypeId) {
      this.ctrlPositionType.setValue(this.data.selectedJobTypeId, { emitEvent: false });
    }
  }

  get totalSteps(): number {
    return 1 + this.groups.length;
  }

  get progress(): number {
    return ((this.stepIndex + 1) / Math.max(this.totalSteps, 1)) * 100;
  }

  get headerTitle(): string {
    if (this.stepIndex === 0) { return 'TIPO DE CARGO'; }
    const g = this.groups[this.stepIndex - 1];
    return g ? g.name.toUpperCase() : '';
  }

  get headerDesc(): string {
    if (this.stepIndex === 0) {
      return 'Selección el tipo de cargo del trabajador al cual se le va a realizar la solicitud e ingreso.';
    }
    const g = this.groups[this.stepIndex - 1];
    return g ? (g.description || '') : '';
  }

  private updateDisplayOptions(): void {
    const g = this.groups[this.stepIndex - 1];
    this.displayOptions = g ? (g.options || []).map((o: any) => ({ Id: o.id, Name: o.name })) : [];
  }

  onSelectPositionType(id: any): void {
    this.ctrlPositionType.setValue(id);
  }

  selectGroupOption(id: any): void {
    const ctrl = this.groupControls[this.stepIndex - 1];
    if (ctrl) {
      ctrl.setValue(id);
    }
  }

  backOrClose(): void {
    if (this.stepIndex === 0) {
      this.dialogRef.close(false);
      return;
    }
    this.stepIndex--;
    this.updateDisplayOptions();
  }

  nextOrSave(): void {
    if (this.stepIndex === 0) {
      const currentId = this.ctrlPositionType.value;

      if (this.committedPositionTypeId !== null && this.committedPositionTypeId !== currentId) {
        this.groups = [];
        this.groupControls = [];
      }

      this.committedPositionTypeId = currentId;
      this.positionTypeConfirmed.emit(currentId);

      if (this.data.adminId && currentId) {
        this.groupsLoading = true;
        this.requestService.getComplianceJobTypeById(this.data.adminId, currentId).subscribe({
          next: (res) => {
            const detail = res?.data || {};
            this.jobTypeDetail = detail;
            const shared: any[] = detail.sharedGroups || [];
            const specific: any[] = detail.specificGroups || [];
            this.groups = [...shared, ...specific];
            // Si el usuario no cambió el tipo de cargo (mismo que venía pre-seleccionado),
            // pre-llenar los controles de grupo con las selecciones guardadas.
            const isSameJobType = currentId === this.data.selectedJobTypeId;
            const savedSelections: Array<{ groupId: any; optionId: any }> = this.data.groupSelections || [];
            this.groupControls = this.groups.map((g) => {
              const saved = isSameJobType ? savedSelections.find(s => s.groupId === g.id) : null;
              return new FormControl(saved?.optionId ?? null, Validators.required);
            });
            if (this.groups.length > 0) {
              this.stepIndex = 1;
              this.updateDisplayOptions();
            }
            this.groupsLoading = false;
            this.cdr.detectChanges();
            if (this.groups.length === 0) {
              this.dialogRef.close({ positionTypeId: currentId, groupSelections: [], directRequirements: detail.directRequirements || [], jobTypeName: detail.name || null });
            }
          },
          error: (err) => {
            this.groupsLoading = false;
            this.stepIndex = 1;
          }
        });
      } else {
        this.dialogRef.close({ positionTypeId: currentId, groupSelections: [] });
      }
      return;
    }

    if (this.stepIndex < this.totalSteps - 1) {
      this.stepIndex++;
      this.updateDisplayOptions();
      return;
    }

    const groupSelections = this.groups.map((g, i) => {
      const optionId = this.groupControls[i]?.value;
      const selectedOption = (g.options || []).find((o: any) => o.id === optionId);
      const optionName = selectedOption?.name || null;
      const optionRequirements: any[] = selectedOption?.requirements || [];
      return {
        groupId: g.id,
        groupName: g.name,
        optionId,
        optionName,
        optionRequirements
      };
    });
    this.dialogRef.close({
      positionTypeId: this.ctrlPositionType.value,
      groupSelections,
      directRequirements: this.jobTypeDetail?.directRequirements || [],
      jobTypeName: this.jobTypeDetail?.name || null
    });
  }

  isNextDisabled(): boolean {
    if (this.stepIndex === 0) { return this.ctrlPositionType.invalid || this.groupsLoading; }
    const ctrl = this.groupControls[this.stepIndex - 1];
    return !ctrl || ctrl.invalid;
  }
}

